import type { TemplateWidgetSpec } from "@/onboarding/templates/types";
import type { DemoWatchlistSpec } from "@/pages/PortfolioBuilder/demoWatchlist";
import type { AssetClassId } from "./types";

/**
 * Starter dashboards for the guided demo.
 *
 * Separate from onboarding/templates because those are built for an entitled
 * trialist and do not survive demo access filtering — Macro & FX drops to a
 * single widget once EconomicCalendar, MarketSnapshot and UsMacroData are
 * gated out, and an equities template loses its whole Equities Suite. Reusing
 * them would hand the visitor a screen that is mostly holes.
 *
 * They cost nothing to apply: no LLM call, no API credits, no network
 * round-trip, so the personalisation can happen the moment the question is
 * answered.
 *
 * Widget choice is constrained by which demo sources actually carry data, not
 * just by which widgets render. Measured against the demo firehose:
 *
 *   fin-x            50 rows    FinX
 *   truth-social     50 rows    TruthSocial
 *   energy-x         50 rows    EnergyX
 *   stocks-two       50 rows    (no demo-accessible widget reads it)
 *   stocks-one        0 rows    -
 *   crypto-web-news   0 rows    CryptoWeb
 *   energy-web-news   0 rows    EnergyWeb
 *
 * So CryptoWeb and EnergyWeb are excluded despite being demoAccessible: the
 * flag says the widget may render, not that anything will be in it. StocksNews
 * is excluded for a different reason — it reads the "stocks-news" source,
 * which is not in the demo source list at all.
 *
 * Re-check with:
 *   curl "<host>/api/dev/content-feeds/news-demo?source=<name>&limit=100"
 *
 * Two rules carried over from the onboarding templates:
 *
 * - The primary Headlines panel is never filtered. The filtered view is a
 *   second instance beside it, so both states are visible at once and the
 *   filter's effect can be seen rather than described.
 * - No TradingView. A free third-party chart shows nothing specific to
 *   Newsquawk and is the widest thing that could occupy the canvas.
 */

// Asset-class tag IDs — mirrors onboarding/templates/index.ts.
const TAG = {
  energy: "195277",
  equities: "195276",
  fixedIncome: "195275",
  forex: "195274",
  metals: "195278",
} as const;

export type DemoTemplate = {
  id: string;
  /** Shown on the chooser card. */
  name: string;
  /** One line, concrete, on the card. */
  blurb: string;
  assetClasses: AssetClassId[];
  widgets: TemplateWidgetSpec[];
  /**
   * Grid id of the filtered Headlines instance, so the guided run can point at
   * it without hard-coding a string in two places.
   *
   * Optional: an asset class with no worthwhile filtered cut of the feed omits
   * it, and the run drops its comparison step rather than pointing at an empty
   * panel. Every current asset class has one.
   */
  filteredHeadlinesId?: string;
  /**
   * The watchlist this asset class builds, and then applies.
   *
   * Terms are the obvious ones for the market — the point is recognition, not
   * completeness: someone should read the chips and think "yes, those are the
   * names", which is what makes applying it mean anything. Keywords follow the
   * desk's house style of bare surnames, matched on word boundaries.
   */
  watchlist: DemoWatchlistSpec;
  /**
   * What the builder box arrives pre-filled with.
   *
   * Written the way a trader would actually say it, not derived from the asset
   * class name — the point of the step is that a sentence is enough, so the
   * sentence has to read like one somebody would type. Submitting it produces
   * `watchlist` above.
   */
  watchlistPrompt: string;
};

const PRIMARY = "NewsquawkHeadlines-primary";

export const demoTemplates: DemoTemplate[] = [
  {
    id: "demo-equities",
    name: "Equities",
    blurb: "Newsquawk headlines, an equities-filtered copy, plus Fin X and Truth Social.",
    assetClasses: ["equities"],
    widgets: [
      { id: PRIMARY, size: { w: 6, h: 12 } },
      {
        id: "NewsquawkHeadlines-equities",
        size: { w: 5, h: 12 },
        filter: { "basic-filter": [TAG.equities] },
      },
      { id: "FinX", size: { w: 5, h: 6 } },
      { id: "TruthSocial", size: { w: 5, h: 6 } },
    ],
    filteredHeadlinesId: "NewsquawkHeadlines-equities",
    watchlist: {
      // Tickers only. "earnings", "guidance" and "buyback" were in here as
      // keywords and did two kinds of damage: they match a large share of the
      // equity wire on their own, so the list stopped being about seven
      // companies — and on screen they sat as chips beside the tickers, which
      // made a list called Magnificent 7 look like ten unrelated terms.
      name: "Magnificent 7",
      tickers: ["NVDA", "AAPL", "MSFT", "GOOGL", "AMZN", "META", "TSLA"],
      keywords: [],
    },
    watchlistPrompt:
      "the megacap tech names — Nvidia, Apple, Microsoft and the rest — and anything on their earnings",
  },
  {
    id: "demo-macro-fx",
    name: "Macro & FX",
    blurb: "Newsquawk headlines, an FX-filtered copy, the calendar, and the policy feeds.",
    assetClasses: ["fx", "macro"],
    widgets: [
      { id: PRIMARY, size: { w: 6, h: 12 } },
      {
        id: "NewsquawkHeadlines-macro",
        size: { w: 5, h: 12 },
        filter: { "basic-filter": [TAG.forex] },
      },
      { id: "TruthSocial", size: { w: 5, h: 6 } },
      { id: "FinX", size: { w: 5, h: 6 } },
      // Live, not locked. The calendar_events endpoint is public and the
      // widget now passes isDemo, so this shows the previous day's releases
      // with real actuals against expectations — which is the thing a macro
      // trader came to look at.
      { id: "EconomicCalendar", size: { w: 6, h: 6 } },
    ],
    filteredHeadlinesId: "NewsquawkHeadlines-macro",
    watchlist: {
      name: "Central Banks & Speakers",
      tickers: [],
      keywords: [
        "Powell", "Lagarde", "Bailey", "Ueda",
        "FOMC", "ECB", "BoE", "BoJ",
        "tariffs", "sanctions",
      ],
    },
    watchlistPrompt:
      "central bank speakers and the policy headlines that move FX",
  },
  {
    // Its own template rather than a line item inside Macro & FX. A rates
    // trader scanning five cards for the one that describes them would not
    // have found it, and the coverage supports a dedicated cut: fixed income
    // tags appear on 15.5% of the delayed feed.
    id: "demo-rates",
    name: "Rates & Fixed Income",
    blurb: "Newsquawk headlines, a rates-filtered copy, and the calendar that moves them.",
    assetClasses: ["fixed_income"],
    widgets: [
      { id: PRIMARY, size: { w: 6, h: 12 } },
      {
        id: "NewsquawkHeadlines-rates",
        size: { w: 5, h: 12 },
        filter: { "basic-filter": [TAG.fixedIncome] },
      },
      // Ahead of the social feeds here: for this asset class the release
      // schedule and the actuals against expectations are the primary surface,
      // not supporting colour.
      { id: "EconomicCalendar", size: { w: 5, h: 6 } },
      { id: "FinX", size: { w: 5, h: 6 } },
    ],
    filteredHeadlinesId: "NewsquawkHeadlines-rates",
    watchlist: {
      name: "Rate Setters & Auctions",
      tickers: [],
      keywords: [
        "Powell", "Lagarde", "Bailey",
        "FOMC", "auction", "yields", "gilt", "Bund", "Treasury",
      ],
    },
    watchlistPrompt:
      "rate setters, auctions and anything moving the curve",
  },
  {
    id: "demo-energy",
    name: "Energy",
    blurb: "Newsquawk headlines, an energy-filtered copy, plus Energy X and Fin X.",
    assetClasses: ["energy"],
    widgets: [
      { id: PRIMARY, size: { w: 6, h: 12 } },
      {
        id: "NewsquawkHeadlines-energy",
        size: { w: 5, h: 12 },
        filter: { "basic-filter": [TAG.energy] },
      },
      { id: "EnergyX", size: { w: 5, h: 6 } },
      { id: "FinX", size: { w: 5, h: 6 } },
    ],
    filteredHeadlinesId: "NewsquawkHeadlines-energy",
    watchlist: {
      name: "OPEC & Majors",
      tickers: ["XOM", "CVX", "SHEL", "BP"],
      keywords: ["OPEC", "crude", "inventories", "Strait of Hormuz", "refinery"],
    },
    watchlistPrompt:
      "OPEC, the oil majors and crude inventories",
  },
  {
    id: "demo-metals",
    name: "Metals",
    blurb: "Newsquawk headlines, a metals-filtered copy, plus Fin X and Truth Social.",
    assetClasses: ["metals"],
    widgets: [
      { id: PRIMARY, size: { w: 6, h: 12 } },
      {
        id: "NewsquawkHeadlines-metals",
        size: { w: 5, h: 12 },
        filter: { "basic-filter": [TAG.metals] },
      },
      { id: "FinX", size: { w: 5, h: 6 } },
      { id: "TruthSocial", size: { w: 5, h: 6 } },
    ],
    filteredHeadlinesId: "NewsquawkHeadlines-metals",
    watchlist: {
      name: "Gold & Miners",
      // No "GOLD" (Barrick), deliberately. keyword_labels is the desk's
      // authoritative identifier field and the ticker matcher trusts it at any
      // length — but the desk also files headlines under topic labels, and one
      // of those is "Gold". Measured over 800 delayed headlines the ticker
      // matched 93 times and 93 of those were the topic: G20 finance
      // ministers, Zelensky phone calls, anything the desk had tagged as
      // gold-adjacent. Barrick is not worth a watchlist that highlights the
      // whole wire. Same trap waits for any ticker spelled like a topic.
      tickers: ["NEM", "FCX", "AEM"],
      keywords: [
        "gold",
        "silver",
        "copper",
        "bullion",
        "platinum",
        "palladium",
        "Barrick",
        "Newmont",
        "miners",
      ],
    },
    watchlistPrompt:
      "gold, silver and the big miners",
  },
];

export function demoTemplateFor(
  assetClass: AssetClassId
): DemoTemplate | undefined {
  return demoTemplates.find((t) => t.assetClasses.includes(assetClass));
}

/** Chooser order — the two biggest asset classes first. */
export const DEMO_TEMPLATE_ORDER: string[] = [
  "demo-macro-fx",
  "demo-rates",
  "demo-equities",
  "demo-energy",
  "demo-metals",
];

export const orderedDemoTemplates: DemoTemplate[] = DEMO_TEMPLATE_ORDER.map(
  (id) => demoTemplates.find((t) => t.id === id)!
).filter(Boolean);
